import { type FC, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";

import { useTheme } from "../../context/ThemeContext";
import { getChartTheme } from "../../theme/chartTheme";
import {
  TIMING_KIND_LABELS,
  type TimingEntry,
  type TimingKind,
} from "../../types/timings";
import { formatDurationMs, toDate } from "../../utils/time";
import { Button } from "../common/Button";
import { ContentCard } from "../common/ContentCard";
import { Text } from "../common/Text";

export interface TimingsTimelineChartProps {
  entries: TimingEntry[];
}

/* ------------------------------------------------------------------ */
/*  Colors                                                             */
/* ------------------------------------------------------------------ */

const KIND_COLORS: Record<TimingKind, { light: string; dark: string }> = {
  model_stage: {
    dark: "rgba(148, 163, 184, 0.80)",
    light: "rgba(71, 85, 105, 0.80)",
  },
  phase: {
    dark: "rgba(52, 211, 153, 0.75)",
    light: "rgba(16, 185, 129, 0.75)",
  },
  run: { dark: "rgba(45, 212, 191, 0.75)", light: "rgba(13, 148, 136, 0.75)" },
  step: { dark: "rgba(245, 158, 11, 0.75)", light: "rgba(217, 119, 6, 0.75)" },
  tool: { dark: "rgba(251, 113, 133, 0.75)", light: "rgba(225, 29, 72, 0.75)" },
};

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

interface ScatterPoint {
  x: number;
  y: number;
  name: string | null;
  kind: TimingKind;
}

interface KindGroup {
  kind: TimingKind;
  label: string;
  color: string;
  points: ScatterPoint[];
}

function groupByKind(entries: TimingEntry[], isDark: boolean): KindGroup[] {
  const groups = new Map<TimingKind, ScatterPoint[]>();

  for (const entry of entries) {
    if (entry.started_at == null) {
      continue;
    }
    const timestamp =
      entry.started_at < 1e12 ? entry.started_at * 1000 : entry.started_at;
    const points = groups.get(entry.kind) ?? [];
    points.push({
      kind: entry.kind,
      name: entry.name,
      x: timestamp,
      y: entry.duration_ms,
    });
    groups.set(entry.kind, points);
  }

  return [...groups.entries()].map(([kind, points]) => ({
    color: isDark ? KIND_COLORS[kind].dark : KIND_COLORS[kind].light,
    kind,
    label: TIMING_KIND_LABELS[kind],
    points,
  }));
}

/* ------------------------------------------------------------------ */
/*  Tooltip                                                            */
/* ------------------------------------------------------------------ */

interface ScatterTooltipPayloadEntry {
  payload: ScatterPoint;
}

interface TimelineTooltipProps {
  active?: boolean;
  payload?: ScatterTooltipPayloadEntry[];
  isDark: boolean;
}

const TimelineTooltip: FC<TimelineTooltipProps> = ({
  active,
  payload,
  isDark,
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const point = payload[0].payload;
  const date = toDate(point.x);

  return (
    <div
      className="rounded-none border px-2.5 py-1.5 text-xs shadow-sm"
      style={{
        backgroundColor: getChartTheme(isDark).tooltipBg,
        borderColor: getChartTheme(isDark).tooltipBorder,
        color: getChartTheme(isDark).tooltipText,
      }}
    >
      <div style={{ color: getChartTheme(isDark).tooltipMuted }}>
        {TIMING_KIND_LABELS[point.kind]}
        {point.name ? ` \u00b7 ${point.name}` : ""}
      </div>
      <div className="font-semibold">{formatDurationMs(point.y)}</div>
      {date && (
        <div style={{ color: isDark ? "rgba(255,255,255,0.40)" : "#9ca3af" }}>
          {date.toLocaleString(undefined, {
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            month: "short",
          })}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const AXIS_TICK_STYLE = { fontSize: 10 };

function formatTickDate(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export const TimingsTimelineChart: FC<TimingsTimelineChartProps> = ({
  entries,
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const groups = useMemo(() => groupByKind(entries, isDark), [entries, isDark]);
  const totalPoints = groups.reduce((sum, g) => sum + g.points.length, 0);

  const { wideRange, maxDuration, logTicks } = useMemo(() => {
    const allY = groups
      .flatMap((g) => g.points.map((p) => p.y))
      .filter((v) => v > 0);
    if (allY.length === 0) {
      return { logTicks: [] as number[], maxDuration: 0, wideRange: false };
    }
    const minV = Math.min(...allY);
    const maxV = Math.max(...allY);
    const wide = maxV / minV > 50;
    const ticks: number[] = [];
    if (wide) {
      for (let p = 0; p <= Math.ceil(Math.log10(maxV * 1.2)); p++) {
        const v = 10 ** p;
        if (v <= maxV * 1.2) {
          ticks.push(v);
        }
      }
    }
    return { logTicks: ticks, maxDuration: maxV, wideRange: wide };
  }, [groups]);

  const [userScale, setUserScale] = useState<"log" | "linear" | null>(null);
  const logScale = userScale === null ? wideRange : userScale === "log";

  if (totalPoints === 0) {
    return (
      <ContentCard className="space-y-3">
        <Text variant="label" as="div">
          Timeline
        </Text>
        <div className="text-sm text-gray-500 dark:text-white/60">
          No entries with timestamps in the current selection.
        </div>
      </ContentCard>
    );
  }

  const { axisText, gridLine } = getChartTheme(isDark);

  return (
    <ContentCard className="space-y-3">
      <div className="flex items-center justify-between">
        <Text variant="label" as="div">
          Timeline
        </Text>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setUserScale(logScale ? "linear" : "log")}
          className="min-w-[5rem] justify-center px-2 py-1 text-slate-500 hover:text-emerald-700 dark:text-slate-400 dark:hover:text-emerald-300"
        >
          {logScale ? "Log" : "Linear"}
        </Button>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <ScatterChart margin={{ bottom: 0, left: -16, right: 4, top: 4 }}>
          <CartesianGrid stroke={gridLine} />
          <XAxis
            type="number"
            dataKey="x"
            domain={["auto", "auto"]}
            tick={AXIS_TICK_STYLE}
            stroke={axisText}
            tickLine={false}
            axisLine={false}
            tickFormatter={formatTickDate}
          />
          <YAxis
            type="number"
            dataKey="y"
            tick={AXIS_TICK_STYLE}
            stroke={axisText}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => formatDurationMs(v)}
            scale={logScale ? "log" : "auto"}
            domain={logScale ? [1, maxDuration * 1.2] : undefined}
            ticks={logScale ? logTicks : undefined}
            allowDataOverflow={logScale}
          />
          <ZAxis range={[40, 40]} />
          <Tooltip content={<TimelineTooltip isDark={isDark} />} />
          <Legend
            iconType="rect"
            iconSize={8}
            wrapperStyle={{ color: axisText, fontSize: 11 }}
          />
          {groups.map((group) => (
            <Scatter
              key={group.kind}
              name={group.label}
              data={group.points}
              fill={group.color}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
    </ContentCard>
  );
};
